/**
 * Page lifecycle handling.
 *
 * - Hidden tab: stop the loop. On return, the loop restarts with a zeroed
 *   accumulator so there is no catch-up burst.
 * - GPU loss (WebGPU device lost, or webglcontextlost on the fallback):
 *   stop the loop, show the "Recovering…" overlay, ask the app to rebuild.
 * - prefers-reduced-motion: reduce: the app starts paused.
 */
import type { Loop } from './loop';

export interface LifecycleHooks {
  loop: Loop;
  /** True while the user has paused. Visibility changes must not unpause. */
  isUserPaused(): boolean;
  /** Rebuild GPU resources. Resolves true on success. */
  rebuildGpu(): Promise<boolean>;
  overlay: HTMLElement;
}

export function prefersReducedMotion(): boolean {
  try {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function installLifecycle(hooks: LifecycleHooks): { notifyGpuLost(reason: string): void } {
  const { loop, overlay } = hooks;
  let recovering = false;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      loop.stop();
    } else if (!hooks.isUserPaused() && !recovering) {
      // start() zeroes the accumulator and the last-frame timestamp.
      loop.start();
    }
  });

  async function recover(reason: string): Promise<void> {
    if (recovering) return;
    recovering = true;
    loop.stop();
    overlay.textContent = 'Recovering…';
    overlay.hidden = false;
    console.warn(`[lifecycle] GPU lost: ${reason}. Rebuilding.`);

    let ok = false;
    try {
      ok = await hooks.rebuildGpu();
    } catch (err) {
      console.error('[lifecycle] rebuild failed', err);
    }
    recovering = false;
    if (ok) {
      overlay.hidden = true;
      if (!hooks.isUserPaused() && !document.hidden) loop.start();
    } else {
      overlay.textContent = 'The GPU context was lost and could not be restored. Reload the page.';
    }
  }

  return {
    notifyGpuLost(reason: string): void {
      void recover(reason);
    },
  };
}
